'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReviewPriority } from '@/lib/ai-review-priority';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import type { PrV2CommentThreadRow, PrV2CommentThreads } from './use-pr-v2-comment-threads';
import type { PrV2FixAll } from './use-pr-v2-fix-all';
import { PrV2BotCommentActions } from './pr-v2-bot-comment-actions';
import { getThreadLocation, getThreadPriority } from './use-pr-v2-comment-threads';
import { Skeleton } from '@/components/ui/skeleton';
import { WorkingDots } from '@/components/ui/working-dots';
import { isTimedOutPendingRun } from '@/data/reviews/review-run-optimistic';
import { useReviewRuns } from '@/data/reviews/use-review-statuses';
import { useNow } from '@/hooks/use-now';
import { cn } from '@/lib/utils';
import { getShortRelativeTime } from '@/lib/utils/date-formatters';
type CommentsFilter = 'all' | 'bots' | 'people';
const COMMENTS_FILTERS: Record<CommentsFilter, {
  label: string;
  noOpen: string;
  none: string;
  getRows: (threads: PrV2CommentThreads) => PrV2CommentThreadRow[];
  getOpenCount: (threads: PrV2CommentThreads) => number;
}> = {
  all: {
    label: 'All',
    noOpen: 'No open comments.',
    none: 'No comments yet.',
    getRows: threads => [...threads.botThreadRows, ...threads.humanThreadRows],
    getOpenCount: threads => threads.openBotCount + threads.openHumanCount
  },
  bots: {
    label: 'Bots',
    noOpen: 'No open bot comments.',
    none: 'No bot comments yet.',
    getRows: threads => threads.botThreadRows,
    getOpenCount: threads => threads.openBotCount
  },
  people: {
    label: 'People',
    noOpen: 'No open comments from people.',
    none: 'No comments from people yet.',
    getRows: threads => threads.humanThreadRows,
    getOpenCount: threads => threads.openHumanCount
  }
};
const PRIORITY_CLASSNAMES: Record<ReviewPriority, string> = {
  P0: 'text-red-300',
  P1: 'text-amber-300',
  P2: 'text-blue-300',
  P3: 'text-zinc-500'
};
function getThreadRowMeta(row: PrV2CommentThreadRow, showSource: boolean, now: number): string {
  return [showSource ? row.sourceLabel : null, getThreadLocation(row), row.createdAt ? getShortRelativeTime(row.createdAt, {
    now
  }) : null, row.replyCount > 0 ? `${row.replyCount} ${row.replyCount === 1 ? 'reply' : 'replies'}` : null, row.status === 'outdated' ? 'outdated' : null].filter(Boolean).join(' · ');
}
function ThreadList(t0) {
  const $ = _c(7);
  const {
    rows,
    onOpenThread
  } = t0;
  const now = useNow();
  let t1;
  if ($[0] !== now || $[1] !== onOpenThread || $[2] !== rows) {
    let t2;
    if ($[4] !== now || $[5] !== onOpenThread) {
      t2 = row => {
        const priority = getThreadPriority(row);
        const meta = getThreadRowMeta(row, priority === null, now);
        return <button key={row.threadId} type="button" data-testid={`pr-v2-comments-thread-row-${row.threadId}`} className="flex w-full min-w-0 gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-zinc-900/60" style={{
          contentVisibility: "auto",
          containIntrinsicSize: "56px"
        }} onClick={() => {
          onOpenThread(row.githubCommentId);
        }}><span className="flex w-5 shrink-0 justify-center pt-0.5">{priority ? <span className={cn("text-[10px] font-semibold leading-4 tracking-[0.04em]", PRIORITY_CLASSNAMES[priority])}>{priority}</span> : <span aria-hidden={true} className={cn("flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-full bg-cover bg-center", !row.userAvatarUrl && "bg-zinc-700 text-[9px] font-semibold text-zinc-200")} style={row.userAvatarUrl ? {
              backgroundImage: `url(${row.userAvatarUrl})`
            } : undefined}>{row.userAvatarUrl ? null : (row.sourceLabel ?? row.userLogin ?? "").charAt(0).toUpperCase()}</span>}</span><span className="flex min-w-0 flex-1 flex-col gap-0.5"><span className={cn("line-clamp-2 text-[13px] leading-[18px]", row.status === "open" ? "text-zinc-200" : "text-zinc-500")}>{row.preview}</span>{meta ? <span className="truncate text-[11px] leading-4 text-zinc-500" title={row.path ?? undefined}>{meta}</span> : null}</span></button>;
      };
      $[4] = now;
      $[5] = onOpenThread;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = rows.map(t2);
    $[0] = now;
    $[1] = onOpenThread;
    $[2] = rows;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function getCollapsedThreadSectionTitle(rows: PrV2CommentThreadRow[]) {
  const resolvedCount = rows.filter(row => row.status === 'resolved').length;
  const outdatedCount = rows.filter(row => row.status === 'outdated').length;
  if (resolvedCount > 0 && outdatedCount > 0) {
    return `${rows.length} closed`;
  }
  if (outdatedCount > 0) {
    return `${rows.length} outdated`;
  }
  return `${rows.length} resolved`;
}
function CollapsedThreadSection(t0) {
  const $ = _c(24);
  const {
    rows,
    onOpenThread
  } = t0;
  const [isExpanded, setIsExpanded] = useState(false);
  if (rows.length === 0) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="mx-2 my-1 h-px bg-zinc-800/60" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsExpanded(_temp);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== rows) {
    t3 = getCollapsedThreadSectionTitle(rows);
    $[2] = rows;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = isExpanded && "rotate-90";
  let t5;
  if ($[4] !== t4) {
    t5 = cn("ml-auto size-3.5 transition-transform", t4);
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t5) {
    t6 = <ChevronRight className={t5} />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== isExpanded || $[9] !== t3 || $[10] !== t6) {
    t7 = <button type="button" className="flex h-8 w-full items-center gap-1.5 rounded-md px-2 text-xs text-zinc-500 hover:bg-zinc-900/60 hover:text-zinc-300" onClick={t2} aria-expanded={isExpanded} data-testid="pr-v2-comments-collapsed-section-toggle">{t3}{t6}</button>;
    $[8] = isExpanded;
    $[9] = t3;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = isExpanded ? "grid-rows-[1fr] pt-2 opacity-100" : "grid-rows-[0fr] pt-0 opacity-0";
  let t9;
  if ($[12] !== t8) {
    t9 = cn("grid transition-[grid-template-rows,opacity,padding-top] duration-75 ease-out", t8);
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = !isExpanded;
  let t11;
  if ($[14] !== onOpenThread || $[15] !== rows) {
    t11 = <div className="min-h-0 overflow-hidden"><ThreadList rows={rows} onOpenThread={onOpenThread} /></div>;
    $[14] = onOpenThread;
    $[15] = rows;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== t10 || $[18] !== t11 || $[19] !== t9) {
    t12 = <div className={t9} inert={t10}>{t11}</div>;
    $[17] = t10;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== t12 || $[22] !== t7) {
    t13 = <div className="w-full">{t1}{t7}{t12}</div>;
    $[21] = t12;
    $[22] = t7;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  return t13;
}
function _temp(current) {
  return !current;
}
function CommentsPanelLoadingSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="pr-v2-cubic-comments-loading-skeleton" className="flex flex-col gap-4 px-3.5 py-3">{["w-5/6", "w-2/3", "w-3/4"].map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(width) {
  return <div key={width} className="flex flex-col gap-1.5"><Skeleton className={cn("h-3.5", width)} /><Skeleton className="h-3 w-1/3" /></div>;
}
type PrV2CommentsPanelProps = {
  owner: string;
  repo: string;
  number: string;
  threads: PrV2CommentThreads;
  fixAll: PrV2FixAll;
  /** Opens a row's thread in the diff. */
  onOpenThread: (githubCommentId: string) => void;
};

/**
 * The comments popover's content: the PR's review threads in one list, filterable to bots or
 * people, with the bot actions (fix with the repository's fix provider, copy a prompt for AI
 * agents).
 */
export function PrV2CommentsPanel(t0) {
  const $ = _c(37);
  const {
    owner,
    repo,
    number,
    threads,
    fixAll,
    onOpenThread
  } = t0;
  const {
    botThreadRows,
    humanThreadRows
  } = threads;
  const [pickedFilter, setPickedFilter] = useState("all");
  const {
    reviewRuns
  } = useReviewRuns(owner, repo, number);
  let t1;
  if ($[0] !== reviewRuns) {
    t1 = reviewRuns.some(_temp3);
    $[0] = reviewRuns;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isReviewRunning = t1;
  const showFilter = botThreadRows.length > 0 && humanThreadRows.length > 0;
  const filter = showFilter ? pickedFilter : "all";
  const {
    noOpen,
    none,
    getRows
  } = COMMENTS_FILTERS[filter];
  let openRows;
  let t2;
  if ($[2] !== getRows || $[3] !== threads) {
    const rows = getRows(threads);
    openRows = rows.filter(_temp4);
    t2 = rows.filter(_temp5);
    $[2] = getRows;
    $[3] = threads;
    $[4] = openRows;
    $[5] = t2;
  } else {
    openRows = $[4];
    t2 = $[5];
  }
  const closedRows = t2;
  const showBotActions = filter !== "people" && threads.openBotCount > 0;
  const showHeader = showFilter || showBotActions;
  if (threads.isLoading) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <CommentsPanelLoadingSkeleton />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    return t3;
  }
  let t3;
  if ($[7] !== botThreadRows || $[8] !== filter || $[9] !== fixAll || $[10] !== showBotActions || $[11] !== showFilter || $[12] !== showHeader || $[13] !== threads) {
    t3 = showHeader ? <div className="flex shrink-0 items-center gap-0.5 px-2 pb-1 pt-1.5">{showFilter ? (Object.keys(COMMENTS_FILTERS) as CommentsFilter[]).map(value => <button key={value} type="button" aria-pressed={filter === value} className={cn("rounded px-1.5 py-0.5 text-xs text-zinc-500 hover:text-zinc-300", filter === value && "text-zinc-100 hover:text-zinc-100")} onClick={() => {
        setPickedFilter(value);
      }}>{COMMENTS_FILTERS[value].label}<span className="ml-1 tabular-nums text-zinc-600">{COMMENTS_FILTERS[value].getOpenCount(threads)}</span></button>) : null}{showBotActions ? <div className="ml-auto"><PrV2BotCommentActions fixAll={fixAll} botThreadRows={botThreadRows} /></div> : null}</div> : null;
    $[7] = botThreadRows;
    $[8] = filter;
    $[9] = fixAll;
    $[10] = showBotActions;
    $[11] = showFilter;
    $[12] = showHeader;
    $[13] = threads;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const t4 = !showHeader && "pt-1.5";
  let t5;
  if ($[15] !== t4) {
    t5 = cn("custom-scrollbar min-h-0 flex-1 overflow-y-auto px-1.5 pb-1.5", t4);
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== filter || $[18] !== isReviewRunning) {
    t6 = isReviewRunning && filter !== "people" ? <p className="flex items-center gap-1.5 px-2 py-1 text-xs text-zinc-500"><WorkingDots ariaLabel="cubic is reviewing" />Review in progress</p> : null;
    $[17] = filter;
    $[18] = isReviewRunning;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== closedRows || $[21] !== noOpen || $[22] !== none || $[23] !== onOpenThread || $[24] !== openRows) {
    t7 = openRows.length > 0 ? <ThreadList rows={openRows} onOpenThread={onOpenThread} /> : <p className="px-2 py-1.5 text-xs text-zinc-500">{closedRows.length > 0 ? noOpen : none}</p>;
    $[20] = closedRows;
    $[21] = noOpen;
    $[22] = none;
    $[23] = onOpenThread;
    $[24] = openRows;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== closedRows || $[27] !== onOpenThread) {
    t8 = <CollapsedThreadSection rows={closedRows} onOpenThread={onOpenThread} />;
    $[26] = closedRows;
    $[27] = onOpenThread;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t3 || $[35] !== t9) {
    t10 = <div className="flex min-h-0 w-full flex-1 flex-col">{t3}{t9}</div>;
    $[34] = t3;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  return t10;
}
function _temp5(row_0) {
  return row_0.status !== "open";
}
function _temp4(row) {
  return row.status === "open";
}
function _temp3(run) {
  return run.status === "running" && !isTimedOutPendingRun(run);
}